/**
 * Os baldes da fila de correção separados por MOTIVO (2026-10-19).
 *
 * Um balde de canal é uma pilha de linhas do mesmo veículo/canal; num
 * upload de imprensa com 300 linhas sem termo de marca em 140 veículos
 * eram 140 cartões empilhados (medido no devstack: 161 baldes, 12 mil
 * pixels), e os 12 veículos não cadastrados, que se resolvem com um
 * cadastro, ficavam enterrados no meio. O motivo é o que diz o que fazer,
 * então é ele que separa as abas.
 *
 * O servidor agrupa por (canal, motivo) — o motivo entrou no agrupamento
 * no mesmo dia, depois de uma planilha sem veículo juntar 43 linhas "sem
 * termo de marca" e 924 de outro erro num balde só, que caía inteiro na
 * aba errada. Então cada balde tem um motivo. `tabOf` ainda decide uma
 * ordem porque valor da planilha e cadastro faltando vêm de colunas
 * próprias (`import_dimension`, `catalog_kind`), além do `failure_kind`.
 */

import type { CorrectionGroup } from "@/shared/api/api-client";
import { foldText } from "@/shared/utils/text-search";
import { importValueName, isImportGroup } from "./import-group";

export type CorrectionTab =
  | "catalog"
  | "vehicle"
  | "imported"
  | "duration"
  | "scrape"
  | "no_brand"
  | "other";

/** Ordem das abas: o que se resolve com um cadastro vem primeiro. */
export const TAB_ORDER: readonly CorrectionTab[] = [
  "catalog",
  "vehicle",
  "imported",
  "duration",
  "scrape",
  "no_brand",
  "other"
];

export const TAB_LABELS: Record<CorrectionTab, string> = {
  catalog: "Cadastro faltando",
  vehicle: "Veículo ambíguo",
  imported: "Valor da planilha",
  duration: "Sem duração",
  scrape: "Falha na coleta",
  no_brand: "Sem termo de marca",
  other: "Outros"
};

/** O que fazer em cada aba, numa frase. */
export const TAB_HINTS: Record<CorrectionTab, string> = {
  catalog:
    "Veículos e canais que não estão no cadastro. Cadastre (ou baixe as pendências e importe nos Ajustes) e reprocesse.",
  vehicle:
    "Há mais de um veículo cadastrado com o nome e a UF e o tipo da planilha não bastaram para separar. Escolha o veículo de cada grupo (vale só para este upload) ou, se nenhum é o certo, cadastre o veículo com esta UF e este tipo e reprocesse.",
  imported:
    "Valores da planilha que não existem no cadastro. Crie no cadastro ou use um existente.",
  duration:
    "TV e rádio sem duração. Corrija a linha na fila abaixo, ou baixe as pendências, corrija a planilha e envie de volta (ou ajuste o mapeamento da coluna no modelo da clipadora).",
  scrape:
    "O texto da matéria não pôde ser coletado. Cole o texto na linha, na fila abaixo, ou baixe as pendências, corrija a planilha e envie de volta; ou reprocesse depois.",
  no_brand:
    "Nenhum termo de marca casou. Se o texto veio incompleto, cole na linha e reprocesse; se não é da empresa, descarte. Com muitas linhas, baixe as pendências, corrija a planilha e envie de volta.",
  other:
    "Linhas que falharam por outro motivo. Veja a mensagem e reprocesse, ou baixe as pendências, corrija a planilha e envie de volta."
};

export function tabOf(group: CorrectionGroup): CorrectionTab {
  if (isImportGroup(group)) return "imported";
  if (group.catalog_kind) return "catalog";
  switch (group.failure_kind) {
    case "vehicle_ambiguous":
      return "vehicle";
    case "missing_duration":
      return "duration";
    case "scrape_failed":
      return "scrape";
    case "no_brand_match":
      return "no_brand";
    default:
      return "other";
  }
}

export type TabSummary = { tab: CorrectionTab; groups: number; lines: number };

/** As abas que têm balde, na ordem, com quantos baldes e quantas linhas. */
export function tabSummaries(groups: readonly CorrectionGroup[]): TabSummary[] {
  const totals = new Map<CorrectionTab, TabSummary>();
  for (const group of groups) {
    const tab = tabOf(group);
    const current = totals.get(tab) ?? { tab, groups: 0, lines: 0 };
    current.groups += 1;
    current.lines += group.pending;
    totals.set(tab, current);
  }
  return TAB_ORDER.flatMap(tab => {
    const summary = totals.get(tab);
    return summary ? [summary] : [];
  });
}

/** O nome que a busca procura: o veículo/canal, ou o valor da planilha. */
function searchableName(group: CorrectionGroup): string {
  return isImportGroup(group) ? importValueName(group) : group.channel ?? "";
}

/** Os baldes de uma aba que casam a busca (sem acento nem caixa, todas as
 *  palavras), na ordem do servidor — mais linhas primeiro. */
export function groupsOfTab(
  groups: readonly CorrectionGroup[],
  tab: CorrectionTab,
  query: string
): CorrectionGroup[] {
  const words = foldText(query).split(/\s+/).filter(Boolean);
  return groups.filter(group => {
    if (tabOf(group) !== tab) return false;
    if (words.length === 0) return true;
    const name = foldText(searchableName(group));
    return words.every(word => name.includes(word));
  });
}
